// P1 · Reading pane + span-highlight overlay (P3). Renders target.headline/body (or
// the reframed body in C5) verbatim, overlays resolved highlight spans, the verbatim
// quote chips (P7), and the flash span driven by a P2 tap. Constant across conditions.

import type { CSSProperties } from "react";
import type { Bundle, Highlight, ReframeResult } from "../types";
import type { DiffPara } from "../diff";
import { INK, INK_DEEP, kindColor, segStyle, flashStyle, insStyle, delStyle, type HighlightStyle } from "../style";

interface ResolvedHl extends Highlight { start: number; end: number; _flash?: boolean; _verbatim?: boolean }

interface Seg {
  kind: "plain" | "verbatim" | "hl";
  text: string;
  hl?: ResolvedHl;
}

export interface ReadingPaneProps {
  b: Bundle;
  body: string;
  headline: string;
  showP3: boolean;
  highlightsOn: boolean;
  highlightStyle: HighlightStyle;
  showP7: boolean;
  reframed: boolean;
  active: ReframeResult | undefined;
  invariantOk: boolean;
  // C5: when a reframe is applied, show its edits as tracked changes over the article itself
  trace?: { paras: DiffPara[]; headlineBefore: string; headlineAfter: string } | null;
  flashAxis: string | null;
  pinnedHl: string | null;
  activeClaim: string | null;
  activeAxis: string | null;
  onToggleHighlights: () => void;
  onHlClick: (id: string, rect: DOMRect) => void;
  onHlHover: (id: string) => void;
  // C5 trace: click a tracked-change span that carries a change_ref → rationale popover
  onTraceOp?: (ref: number, rect: DOMRect) => void;
  activeTraceRef?: number | null;
}

export function ReadingPane(p: ReadingPaneProps) {
  const { b, body } = p;
  const T = b.target;

  // ---- assemble the overlay set ----
  let overlay: Partial<ResolvedHl>[] = (p.showP3 && p.highlightsOn) ? b.highlights.slice() : [];

  // verbatim quote chips (P7): mark each direct quote present in the body
  if (p.showP7 && p.invariantOk) {
    for (const q of b.fact_table.direct_quotes || []) {
      if (q && body.indexOf(q) >= 0 && !overlay.some((h) => h.quote === q)) {
        overlay = [...overlay, { id: "__verbatim_" + q.slice(0, 8), quote: q, kind: "verbatim", _verbatim: true, explanation: "Verbatim quote, identical across every framing." } as Partial<ResolvedHl>];
      }
    }
  }

  // flash span from a P2 tap
  if (p.flashAxis) {
    const fa = b.axis_profile.find((a) => a.axis === p.flashAxis);
    const fq = fa?.evidence_quote;
    if (fq) {
      if (overlay.some((h) => h.quote === fq)) overlay = overlay.map((h) => (h.quote === fq ? { ...h, _flash: true } : h));
      else overlay = [...overlay, { id: "__flash", quote: fq, kind: (fa?.evidence_kind || "axis"), explanation: fa?.lean_label || "", _flash: true } as Partial<ResolvedHl>];
    }
  }

  // resolve to offsets, drop overlaps, build paragraphs
  const resolved: ResolvedHl[] = [];
  overlay.forEach((h) => {
    const i = body.indexOf(h.quote!);
    if (i >= 0) resolved.push({ ...(h as ResolvedHl), start: i, end: i + h.quote!.length });
  });
  resolved.sort((a, b2) => a.start - b2.start);
  const nonOv: ResolvedHl[] = [];
  let last = -1;
  resolved.forEach((h) => { if (h.start >= last) { nonOv.push(h); last = h.end; } });

  const flat: Seg[] = [];
  let cur = 0;
  nonOv.forEach((h) => {
    if (h.start > cur) flat.push({ kind: "plain", text: body.slice(cur, h.start) });
    flat.push({ kind: h._verbatim ? "verbatim" : "hl", text: body.slice(h.start, h.end), hl: h });
    cur = h.end;
  });
  if (cur < body.length) flat.push({ kind: "plain", text: body.slice(cur) });

  // split into paragraphs on \n\n (only inside plain segments)
  const paras: Seg[][] = [[]];
  flat.forEach((seg) => {
    if (seg.kind === "plain") {
      const parts = seg.text.split("\n\n");
      parts.forEach((pt, idx) => { if (idx > 0) paras.push([]); if (pt) paras[paras.length - 1].push({ kind: "plain", text: pt }); });
    } else {
      paras[paras.length - 1].push(seg);
    }
  });
  const paragraphs = paras.filter((pp) => pp.length);

  const isActive = (h: ResolvedHl) =>
    p.pinnedHl === h.id ||
    (!!h.cross_outlet && p.activeClaim === h.cross_outlet.claim_id) ||
    (h.kind === "axis" && p.activeAxis === h.ref);

  const traceOn = !!p.trace;
  const h1Style: CSSProperties = { fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: 31, lineHeight: 1.18, letterSpacing: "-.015em", margin: "0 0 10px", textWrap: "balance" as any, color: INK_DEEP };

  return (
    <article style={{ background: "#fff", border: "1px solid rgba(55,53,47,.12)", borderRadius: 6, padding: "38px 44px 40px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, paddingBottom: 14, marginBottom: 22, borderBottom: "1px solid rgba(55,53,47,.09)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 20, height: 20, borderRadius: 4, background: INK, color: "#fff", fontSize: 9.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{outletInitial(T.outlet)}</span>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: "rgba(55,53,47,.7)" }}>{T.outlet_display}</span>
        </div>
        <span style={{ fontSize: 11.5, color: "rgba(55,53,47,.4)", fontWeight: 500 }}>{b.incident.date_label || b.incident.date}</span>
      </div>

      {p.reframed && p.active && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: "7px 11px", background: SURFACE_WARM, border: "1px solid rgba(55,53,47,.14)", borderLeft: "3px solid " + INK, borderRadius: 4 }}>
          <span style={{ fontSize: 11 }}>⚗</span>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: INK }}>AI-slanted study material: "{p.active.display}" framing.</span>
          <span style={{ fontSize: 11.5, color: "rgba(55,53,47,.55)" }}>Facts unchanged; only the framing was moved.</span>
        </div>
      )}

      {traceOn && p.trace!.headlineBefore !== p.trace!.headlineAfter ? (
        <div style={{ marginBottom: 10 }}>
          <h1 style={{ ...h1Style, margin: "0 0 4px", fontWeight: 600, color: "rgba(55,53,47,.42)", textDecoration: "line-through", textDecorationColor: "rgba(55,53,47,.3)" }}>{p.trace!.headlineBefore}</h1>
          <h1 style={h1Style}>{p.trace!.headlineAfter}</h1>
        </div>
      ) : (
        <h1 style={h1Style}>{p.headline}</h1>
      )}
      <div style={{ fontSize: 12, color: "rgba(55,53,47,.42)", marginBottom: 26, fontWeight: 600, letterSpacing: ".01em" }}>{b.incident.city_and_state}</div>

      <div style={{ fontSize: 17, lineHeight: 1.72, color: INK, letterSpacing: "-.002em" }}>
        {traceOn
          ? p.trace!.paras.map((para) => (
              // "t"-prefixed keys: never let React style-diff trace buttons against the
              // highlight-mode buttons (their style objects mix shorthand/longhand props)
              <p key={"t" + para.key} style={{ margin: "0 0 17px" }}>
                {para.ops.map((op, i) => {
                  if (op.isEqual) return <span key={i}>{op.text}</span>;
                  const act = op.change_ref != null && p.activeTraceRef === op.change_ref;
                  const st = op.opType === "insert" ? insStyle(act) : delStyle(act);
                  // Same policy as the P5 diff: only ops that resolve a change_log entry
                  // are clickable buttons (rationale popover); the rest stay plain spans.
                  if (op.change_ref == null || !p.onTraceOp) return <span key={i} style={{ ...st, cursor: "default" }}>{op.text}</span>;
                  return (
                    <button key={i} onClick={(e) => p.onTraceOp!(op.change_ref!, e.currentTarget.getBoundingClientRect())} style={st}>
                      {op.text}
                    </button>
                  );
                })}
              </p>
            ))
          : paragraphs.map((segs, pi) => (
          <p key={pi} style={{ margin: "0 0 17px" }}>
            {segs.map((seg, si) => {
              if (seg.kind === "plain") return <span key={si}>{seg.text}</span>;
              if (seg.kind === "verbatim") {
                return (
                  <span key={si} title={seg.hl!.explanation}
                    style={{ background: "rgba(55,53,47,.05)", borderBottom: "1.5px dotted rgba(55,53,47,.4)", borderRadius: 2, padding: "0 1px", cursor: "help" }}>
                    {seg.text}
                  </span>
                );
              }
              const h = seg.hl!;
              const act = isActive(h);
              const c = kindColor(h.kind);
              const supStyle: CSSProperties = { fontSize: 7, color: c.fg, marginLeft: 1, verticalAlign: "super", lineHeight: 0 };
              return (
                <button key={si} data-hl-id={h.id} data-flash={h._flash ? "1" : "0"}
                  onClick={(e) => p.onHlClick(h.id, e.currentTarget.getBoundingClientRect())}
                  onMouseEnter={() => p.onHlHover(h.id)}
                  aria-label={(h.kind === "device" ? "Loaded language: " : "Framing axis: ") + h.explanation}
                  style={h._flash ? flashStyle(h.kind) : segStyle(h.kind, p.highlightStyle, act)}>
                  {seg.text}<sup style={supStyle}>{h.kind === "device" ? "◆" : "▲"}</sup>
                </button>
              );
            })}
          </p>
        ))}
      </div>

      {traceOn && (
        <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid rgba(55,53,47,.09)", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", fontSize: 11.5, color: "rgba(55,53,47,.55)" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#176c3a", fontWeight: 700, borderBottom: "2px solid #1f7a44" }}>added</span> wording toward the chosen framing</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#a4322b", textDecoration: "line-through" }}>removed</span> from the article as published</span>
        </div>
      )}

      {!traceOn && p.showP3 && (
        <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid rgba(55,53,47,.09)", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <button onClick={p.onToggleHighlights}
            style={{ font: "inherit", fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 6, cursor: "pointer", border: "1px solid " + (p.highlightsOn ? INK : "rgba(55,53,47,.16)"), background: p.highlightsOn ? INK : "#fff", color: p.highlightsOn ? "#fff" : "rgba(55,53,47,.65)" }}>
            {p.highlightsOn ? "Hide highlights" : "Show highlights"}
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 11.5, color: "rgba(55,53,47,.55)" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontWeight: 700, color: "#b3261e" }}>◆</span><span style={{ borderBottom: "2px solid #b3261e", paddingBottom: 1 }}>Loaded language / device</span></span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontWeight: 700, color: "#1f7a44" }}>▲</span><span style={{ borderBottom: "2px dashed #1f7a44", paddingBottom: 1 }}>Framing axis</span></span>
          </div>
        </div>
      )}
    </article>
  );
}

const SURFACE_WARM = "#f7f6f3";
function outletInitial(o: string): string {
  const known: Record<string, string> = { "miamiherald.com": "MH", "cbsmiami.com": "CBS", "local10.com": "L10" };
  return known[o] || (o || "").slice(0, 3).toUpperCase();
}
